// Faithful transcription of the hardcoded legal text in PolicyPages.jsx into
// the markdown-lite format lib/policyMarkdown.js understands. Used ONLY to
// pre-fill the admin editor (PolicyContentAdmin in AdminPage.jsx) so a super
// admin sees and edits the real current wording instead of a blank box.
//
// This is intentionally NOT wired into DEFAULT_POLICY_CONTENT / the public
// PolicyPages.jsx fallback check — until a super admin actually hits Save,
// the live site keeps rendering the original hardcoded JSX unchanged. Once
// saved (even unedited), that page switches to rendering this text via the
// markdown-lite parser instead.
//
// Known fidelity gaps versus the original JSX (documented, not accidental):
// - No inline line-break support in the parser, so multi-line contact blocks
//   (address / phone / email) become separate paragraphs instead of tightly
//   stacked lines — slightly more vertical spacing than the original.
// - Nested sub-lists (e.g. Shipping's Standard/Express delivery times) are
//   folded into the parent bullet's sentence rather than rendered as a
//   nested list, since the parser only supports one list level.
const ADDRESS = "Annapurna Home Pickles, 2-283/3, Kadiyapusavaram, Kadiam, Rajahmundry Rural, East Godavari District, Andhra Pradesh – 533101, India";
const PHONE_LINE = "Phone: [+91 92811 05088](tel:+919281105088)";
const EMAIL_LINE = "Email: [annapurnahomepickles@zohomail.in](mailto:annapurnahomepickles@zohomail.in)";

export const POLICY_SEED_CONTENT = {
  terms: `This document is an electronic record in terms of the Information Technology Act, 2000 and rules made thereunder, as applicable, and the amended provisions pertaining to electronic records in various statutes as amended by the Information Technology Act, 2000. This electronic record is generated by a computer system and does not require any physical or digital signatures.

This document is published in accordance with the provisions of Rule 3 (1) of the Information Technology (Intermediaries Guidelines) Rules, 2011, which require publishing the rules and regulations, privacy policy, and Terms of Use for access or usage of the domain [https://www.ahpickles.in/](https://www.ahpickles.in/) ("Website"), including any related mobile site and mobile application (collectively, the "Platform").

The Platform is owned and operated by **Annapurna Home Pickles**, with its registered office at **${ADDRESS}** (hereinafter referred to as the "Platform Owner", "we", "us", "our").

Your use of the Platform and our services and tools is governed by the following terms and conditions ("Terms of Use") as applicable to the Platform, including the applicable policies which are incorporated herein by way of reference. If You transact on the Platform, You shall be subject to the policies applicable to the Platform for such transaction. By mere use of the Platform, You shall be contracting with the Platform Owner, and these terms and conditions, including the policies, constitute Your binding obligations with the Platform Owner. These Terms of Use relate to your use of our website and services (collectively, "Services"). Any terms and conditions proposed by You which are in addition to or which conflict with these Terms of Use are expressly rejected by the Platform Owner and shall be of no force or effect. These Terms of Use can be modified at any time without assigning any reason. It is your responsibility to periodically review these Terms of Use to stay informed of updates.

For the purpose of these Terms of Use, wherever the context so requires, "you", "your" or "user" shall mean any natural or legal person who has agreed to become a user/buyer on the Platform.

**ACCESSING, BROWSING, OR OTHERWISE USING THE PLATFORM INDICATES YOUR AGREEMENT TO ALL THE TERMS AND CONDITIONS UNDER THESE TERMS OF USE, SO PLEASE READ THE TERMS OF USE CAREFULLY BEFORE PROCEEDING.**

## Terms of Use

The use of the Platform and/or availing of our Services is subject to the following Terms of Use:

1. To access and use the Services, you agree to provide true, accurate, and complete information to us during and after registration, and you shall be responsible for all acts done through the use of your registered account on the Platform.
1. Neither we nor any third parties provide any warranty or guarantee as to the accuracy, timeliness, performance, completeness, or suitability of the information and materials offered on this website or through the Services for any specific purpose. You acknowledge that such information and materials may contain inaccuracies or errors, and we expressly exclude liability for any such inaccuracies or errors to the fullest extent permitted by law.
1. Your use of our Services and the Platform is solely and entirely at your own risk and discretion, for which we shall not be liable to you in any manner. You are required to independently assess and ensure that the Services meet your requirements.
1. The contents of the Platform and the Services are proprietary to us and are licensed to us. You will not have any authority to claim any intellectual property rights, title, or interest in its contents, including but not limited to the design, layout, look, and graphics.
1. You acknowledge that unauthorized use of the Platform and/or the Services may lead to action against you as per these Terms of Use and/or applicable laws.
1. You agree to pay us the charges associated with availing the Services.
1. You agree not to use the Platform and/or Services for any purpose that is unlawful, illegal, or forbidden by these Terms, or by Indian or local laws that might apply to you.
1. You agree and acknowledge that the website and Services may contain links to other third-party websites. On accessing these links, you will be governed by the terms of use, privacy policy, and other policies of such third-party websites. These links are provided for your convenience to provide further information.
1. You understand that upon initiating a transaction for availing the Services, you are entering into a legally binding and enforceable contract with the Platform Owner for the Services.
1. You shall indemnify and hold harmless the Platform Owner, its affiliates, group companies (as applicable), and their respective officers, directors, agents, and employees from any claim or demand, or actions including reasonable attorney's fees, made by any third party or penalty imposed due to or arising out of Your breach of these Terms of Use, the Privacy Policy, and other Policies, or Your violation of any law, rules, or regulations, or the rights (including infringement of intellectual property rights) of a third party.
1. Notwithstanding anything contained in these Terms of Use, the parties shall not be liable for any failure to perform an obligation under these Terms if performance is prevented or delayed by a force majeure event.
1. These Terms and any dispute or claim relating to them, or their enforceability, shall be governed by and construed in accordance with the laws of India.
1. All disputes arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts in Rajahmundry, Andhra Pradesh.
1. All concerns or communications relating to these Terms must be communicated to us using the contact information below.

## Contact Us

${ADDRESS}

${PHONE_LINE}

${EMAIL_LINE}`,

  privacy: `## Introduction

This Privacy Policy describes how **Annapurna Home Pickles** ("we", "our", "us") collects, uses, shares, protects, or otherwise processes your information/personal data through our website [https://www.ahpickles.in/](https://www.ahpickles.in/) (the "Platform"). You may browse certain sections of the Platform without registering with us. We do not offer any product/service under this Platform outside India, and your personal data will primarily be stored and processed in India. By visiting this Platform, providing your information, or availing any product/service offered on the Platform, you expressly agree to be bound by the terms and conditions of this Privacy Policy, the Terms of Use, and the applicable service/product terms and conditions, and agree to be governed by the laws of India, including but not limited to the laws applicable to data protection and privacy. If you do not agree, please do not use or access our Platform.

## Collection

We collect your personal data when you use our Platform, our services, or otherwise interact with us during the course of our relationship, and related information provided from time to time. Some of the information we may collect includes, but is not limited to, personal data provided to us during sign-up/registration or while using our Platform, such as name, date of birth, address, telephone/mobile number, email ID, and/or any such information shared as proof of identity or address. Some sensitive personal data may be collected with your consent, such as your bank account, credit/debit card, or other payment instrument information, in accordance with applicable law. You always have the option not to provide information by choosing not to use a particular service or feature on the Platform. We may track your behaviour, preferences, and other information you choose to provide on our Platform, compiled and analysed on an aggregated basis. If you receive an email or a call from a person/association claiming to be Annapurna Home Pickles and seeking personal data such as your debit/credit card PIN or net-banking/mobile banking password, please do not provide such information and report it to an appropriate law enforcement agency.

## Usage

We use personal data to provide the services you request. To the extent we use your personal data to market to you, we will provide you the ability to opt out of such uses. We use your personal data to fulfil orders, enhance customer experience, resolve disputes, troubleshoot problems, inform you about offers, products, services and updates, detect and protect against error, fraud, and other criminal activity, enforce our terms and conditions, and conduct marketing research, analysis, and surveys.

## Sharing

We may disclose personal data to third parties such as logistics/courier partners and payment processors where required to fulfil your order, comply with our legal obligations, or prevent, detect, and investigate fraudulent or illegal activities. We may disclose personal and sensitive personal data to government agencies or other authorised law enforcement agencies if required to do so by law, or in the good-faith belief that such disclosure is reasonably necessary to respond to subpoenas, court orders, or other legal process, or to protect the rights, property, or personal safety of our users or the general public.

## Security Precautions

To protect your personal data from unauthorised access or disclosure, loss, or misuse, we adopt reasonable security practices and procedures. However, the transmission of information over the internet is not completely secure for reasons beyond our control, and certain inherent risks regarding the use of the Platform always remain. You are responsible for ensuring the protection of your login and password records.

## Data Deletion and Retention

You have the option to delete your account by visiting your profile and settings on our Platform; this action will result in you losing all information related to your account. We retain your personal data for a period no longer than is required for the purpose for which it was collected, or as required under applicable law, except where we believe retention is necessary to prevent fraud or future abuse, or for other legitimate purposes.

## Your Rights

You may access, rectify, and update your personal data directly through the functionality provided on the Platform, or by writing to us using the contact information below.

## Consent

By visiting our Platform or providing your information, you consent to the collection, use, storage, disclosure, and processing of your information in accordance with this Privacy Policy. You may withdraw consent previously provided by writing to the Grievance Officer at the contact information below, with the subject line "Withdrawal of consent for processing personal data". Please note that withdrawal of consent will not be retrospective and will be in accordance with the Terms of Use, this Privacy Policy, and applicable laws.

## Changes to this Privacy Policy

Please check our Privacy Policy periodically for changes. We may update this Privacy Policy to reflect changes to our information practices, and will notify you of significant changes as required under applicable law.

## Grievance Officer

Name: Gubbala Chiru Mani Sai Raj

Designation: Grievance Officer

${ADDRESS}

Phone: [+91 92811 05088](tel:+919281105088) (Monday – Friday, 9:00 AM – 6:00 PM)

${EMAIL_LINE}`,

  shipping: `At **Annapurna Home Pickles**, we take pride in preparing and packaging our homemade pickles fresh for every order. Because our products are freshly prepared, orders are dispatched only after preparation and quality packaging are fully complete to ensure they reach you in perfect condition.

## 1. Shipping Methods & Costs

- **Our Carriers:** Orders are shipped exclusively through registered domestic courier companies and/or India Post Speed Post.
- **Shipping Fees:** Any applicable shipping or delivery costs are calculated based on your delivery address and order value. These costs are displayed clearly at checkout before you complete your payment.
- **Non-Refundable Policy:** Shipping fees, where levied, are non-refundable once the order has been shipped.

## 2. Estimated Delivery Time

- **Dispatch Window:** Orders are freshly prepared, securely packed, and shipped within **1 to 3 business days** from the date of the order and/or payment confirmation (or as per the delivery date agreed upon at the time of order confirmation).
- **Delivery Timelines:** Once handed over to the courier partner, actual delivery times depend on your location — Standard Delivery takes 4 to 7 business days, and Express Delivery takes 2 to 4 business days.
- **Delays & Liability:** Actual transit times are subject to courier company or postal authority norms. Annapurna Home Pickles shall not be liable for delivery delays caused by the courier company or postal authority once the package has been handed over for shipping.

## 3. Order Tracking

- **Tracking Details:** Once your order is dispatched, a courier tracking ID and/or tracking link will be added to your order. You can view this by navigating to [My Orders](/my-orders) and selecting the order.
- **Status Updates:** Real-time shipping status updates will also be sent automatically to the email ID you provided at checkout.
- **Delivery Address:** All deliveries will be made strictly to the address provided by the buyer at the time of purchase. Please double-check your shipping address before finalising your order.

## 4. Shipping Coverage

- **Domestic Shipping:** We currently ship all across India.
- **Remote Areas:** Delivery to remote or rural pin codes may take a few additional days depending on our courier partner's specific regional coverage and transit schedules.

## 5. Contact Us

For any shipping, tracking, or delivery-related queries, please feel free to reach out to us at:

**Annapurna Home Pickles**

Contact Person: CHIRUMANI SAI RAJA GUBBALA

2-283/3, Kadiyapusavaram, Kadiam, Rajahmundry Rural, East Godavari District, Andhra Pradesh – 533101, India

${PHONE_LINE}

${EMAIL_LINE}

Website: [https://www.ahpickles.in/](https://www.ahpickles.in/)`,

  refund: `This Refund Policy outlines how **Annapurna Home Pickles** handles refunds for orders placed through [https://www.ahpickles.in/](https://www.ahpickles.in/).

## Eligibility for Refunds

Refunds are only granted under the following circumstances:

- The product arrives **damaged** (broken or leaking jar/packaging).
- An **incorrect item** was sent (different flavour or size than ordered).
- The order was **successfully cancelled** before it was shipped.

## Reporting Window

You must report any damage or incorrect items within **24 hours** of delivery. Requests raised after this window will not be eligible for a refund or replacement.

## Proof Required

To process your request, please provide:

- Your **Order ID** (found in your order confirmation or on the [My Orders](/my-orders) page).
- Clear **photographic or video evidence** of the damage or incorrect product.

## Approval Notice

Our team will review the details submitted and notify you via email or text message regarding the approval or rejection of your refund request.

## Processing Time

Once approved, the refund will be processed by our team within **2 to 3 business days**.

## Payment Method

The refund will be credited exclusively to the **original payment method** used during the purchase. We are unable to issue refunds to a different account or payment method.

## Bank Timeline

Please allow up to **7 business days** for the credited amount to reflect in your bank account or card statement, depending on your financial institution.

## Contact Us

To raise a refund request or for any related queries, please reach us at:

${ADDRESS}

${PHONE_LINE}

${EMAIL_LINE}`,

  cancellation: `This Cancellation Policy describes the conditions under which orders placed with **Annapurna Home Pickles** through [https://www.ahpickles.in/](https://www.ahpickles.in/) can be cancelled.

## Time Window

Cancellation requests are accepted only within **3 days** of placing your order.

## Shipping Status

Orders can only be cancelled **before** they have been shipped or handed over to our courier partner. Once an order has been dispatched, cancellation is no longer possible through our system.

> **Important — Shipped Orders:** If your order has already been shipped or is out for delivery, it cannot be cancelled. Your only option at this stage is to **reject the package at your doorstep** when the delivery partner arrives. Once rejected, please contact us so we can process the next steps.

## How to Request a Cancellation

To cancel an eligible order, please contact us immediately with your **Order Number** (found on your order confirmation or on the [My Orders](/my-orders) page):

- WhatsApp: [+91 92811 05088](https://wa.me/919281105088)
- Email: [annapurnahomepickles@zohomail.in](mailto:annapurnahomepickles@zohomail.in)

## Contact Us

For any cancellation queries, please reach us at:

${ADDRESS}

${PHONE_LINE}

${EMAIL_LINE}`,

  return: `This Return Policy describes the conditions under which products purchased from **Annapurna Home Pickles** through [https://www.ahpickles.in/](https://www.ahpickles.in/) may be eligible for a refund.

## Strict No-Return Policy

Due to the perishable nature of food products and for health and hygiene reasons, we do **not accept returns** on any of our pickles once they have been successfully delivered. We encourage you to review your order carefully before checking out. If you have questions about ingredients or spice levels, please reach out to us before purchasing.

## Damaged or Incorrect Items

While we pack our pickles securely, we will gladly make things right if your order arrives under the following conditions:

- **Damaged Products:** The jar or packaging is broken, leaking, or visibly compromised upon delivery.
- **Incorrect Products:** You received a different pickle flavour or size than what you ordered.

## How to Claim a Refund

1. Notify us within **24 hours** of receiving your package.
1. Email us at [annapurnahomepickles@zohomail.in](mailto:annapurnahomepickles@zohomail.in) or message us on WhatsApp at [+91 92811 05088](https://wa.me/919281105088).
1. Provide your **Order Number** and attach clear photographic or video evidence of the damage or incorrect item.

Approved claims will be processed as a refund in accordance with our [Refund Policy](/refund-policy).

## Contact Us

For any return or refund queries, please reach us at:

${ADDRESS}

${PHONE_LINE}

${EMAIL_LINE}`,
};
